/**
 * Eros PDP trust badges — concern strip (ADR-173 dictionary + bucket detection).
 * Fills [data-eros-trust-concerns-host] inside [data-eros-pdp-trust]. Fail soft.
 * Wave 24B — circle tap opens sheet/dialog with tooltip copy (not title-only).
 */
(function () {
  'use strict';

  /* Mirror Flutter makeupTypeFamilyByType — no powder/cc_cream extras (wpm_29). */
  var FACE_TYPES = {
    foundation: 1,
    bb_cream: 1,
    concealer: 1,
    face_powder: 1,
    primer: 1,
    setting_spray: 1,
    setting_powder: 1,
    highlighter: 1,
    contour: 1,
    blush: 1,
    makeup_fixer: 1,
  };

  var EYE_TYPES = {
    eyeliner: 1,
    mascara: 1,
    eyeshadow_palette: 1,
    eyeshadow: 1,
    kohl: 1,
    false_eyelashes: 1,
    lash_glue: 1,
    brow_pencil: 1,
    brow_gel: 1,
    brow_wax: 1,
    brow_palette: 1,
    brow_filler: 1,
    brow_glue: 1,
  };

  var LIP_TYPES = {
    lipstick: 1,
    lip_balm: 1,
    lip_liner: 1,
    lip_gloss: 1,
    lip_oil: 1,
    lip_tint: 1,
    cheek_lip_tint: 1,
  };

  var CARE_COMFORT_SUFFIXES = [
    '_cleanser',
    '_cleansing',
    'makeup_remover',
    'micellar',
    'face_wash',
    'body_wash',
    'shower_gel',
  ];

  var CARE_TREATMENT_TOKENS = [
    'serum',
    'treatment',
    'sunscreen',
    'moisturizer',
    'cream',
    'mask',
    'toner',
    'essence',
    'ampoule',
    'peel',
    'spot',
    'eye_cream',
    'lip_treatment',
  ];

  var HAIR_TOKENS = ['shampoo', 'conditioner', 'hair_', 'scalp', 'lash_serum'];
  var ORAL_TOKENS = ['toothpaste', 'mouthwash', 'dental', 'tooth_', 'oral'];

  var ICON_PATHS = {
    droplet:
      'M12 2.7c0 0-7 7.2-7 12.1A7 7 0 0 0 12 21.8a7 7 0 0 0 7-7c0-4.9-7-12.1-7-12.1z',
    check:
      'M9.2 16.6L4.8 12.2l1.4-1.4 3 3 8-8 1.4 1.4z',
    heart:
      'M12 20.4s-7.2-4.5-7.2-9.1A4.1 4.1 0 0 1 12 8.4a4.1 4.1 0 0 1 7.2 2.9c0 4.6-7.2 9.1-7.2 9.1z',
    sparkle:
      'M12 3l1.4 5.2L18.6 9.6l-5.2 1.4L12 16.2l-1.4-5.2L5.4 9.6l5.2-1.4L12 3zm6.5 8.5l.7 2.6 2.6.7-2.6.7-.7 2.6-.7-2.6-2.6-.7 2.6-.7.7-2.6z',
  };

  function norm(s) {
    return String(s || '')
      .trim()
      .toLowerCase();
  }

  function endsWithAny(type, suffixes) {
    for (var i = 0; i < suffixes.length; i++) {
      if (type.indexOf(suffixes[i]) === type.length - suffixes[i].length) {
        return true;
      }
      if (type === suffixes[i]) return true;
    }
    return false;
  }

  function containsAny(type, tokens) {
    for (var i = 0; i < tokens.length; i++) {
      if (type.indexOf(tokens[i]) !== -1) return true;
    }
    return false;
  }

  function bucketFromType(type) {
    if (!type) return null;
    if (type === 'makeup_remover') return 'care_comfort';
    if (FACE_TYPES[type]) return 'makeup_face_base';
    if (EYE_TYPES[type]) return 'makeup_eye';
    if (LIP_TYPES[type]) return 'makeup_lip';
    if (endsWithAny(type, CARE_COMFORT_SUFFIXES)) return 'care_comfort';
    if (containsAny(type, HAIR_TOKENS)) return 'hair_care';
    if (containsAny(type, ORAL_TOKENS)) return 'oral_care';
    if (containsAny(type, CARE_TREATMENT_TOKENS)) return 'care_treatment';
    return null;
  }

  function bucketFromSection(section) {
    switch (section) {
      case 'makeup':
        return 'makeup_face_base';
      case 'hair_care':
        return 'hair_care';
      case 'oral_care':
        return 'oral_care';
      case 'face_care':
      case 'eye_care':
      case 'body_care':
        return 'care_treatment';
      /* skin_care → Flutter fallback (not care_treatment) */
      default:
        return 'fallback';
    }
  }

  function pdpConcernBucket(section, type) {
    var t = norm(type);
    if (t) {
      var fromType = bucketFromType(t);
      if (fromType) return fromType;
    }
    var s = norm(section);
    if (s) return bucketFromSection(s);
    return 'fallback';
  }

  function parseConcerns(raw) {
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === 'string') {
      var s = raw.trim();
      if (!s) return [];
      try {
        var parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed.map(String);
      } catch (e) {
        /* comma-separated fallback */
      }
      return s
        .split(',')
        .map(function (x) {
          return x.trim();
        })
        .filter(Boolean);
    }
    return [];
  }

  function localeSuffix() {
    var lang = String(document.documentElement.lang || 'ar')
      .trim()
      .toLowerCase()
      .split('-')[0];
    if (lang === 'en' || lang === 'ku') return lang;
    return 'ar';
  }

  function labelFromCopy(copy) {
    if (!copy) return '';
    var loc = localeSuffix();
    var localized = copy['short_label_' + loc];
    if (localized) return localized;
    return copy.short_label_ar || '';
  }

  function tooltipFromCopy(copy, label) {
    if (!copy) return label;
    var loc = localeSuffix();
    var localized = copy['tooltip_' + loc];
    if (localized) return localized;
    return copy.tooltip_ar || label;
  }

  function resolveCopy(entries, concern, bucket) {
    if (!entries || !concern) return null;
    var rows = entries[concern];
    if (!rows) return null;
    var primary = rows[bucket];
    if (primary) {
      if (primary.show === false) return null;
      if (primary.show === true && primary.short_label_ar) return primary;
      return null;
    }
    var fb = rows.fallback;
    if (fb && fb.show !== false && fb.short_label_ar) return fb;
    return null;
  }

  /*
   * Exact per-concern Material icon paths, ported from
   * pdp_concerns_badge_content.dart's _iconForConcern() switch, keyed by the
   * raw `eros.concerns` metafield code (see product_concern.dart's
   * productConcernFromRaw() for the code->enum mapping used to derive this).
   * Several codes intentionally share the same icon, same as Flutter.
   */
  var CONCERN_ICON_PATHS = {
    // Icons.favorite_border
    sensitive_skin:
      'M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3C4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5C22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1l-.1-.1C7.14 14.24 4 11.39 4 8.5C4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5c0 2.89-3.14 5.74-7.9 10.05z',
    gum_health:
      'M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3C4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5C22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1l-.1-.1C7.14 14.24 4 11.39 4 8.5C4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5c0 2.89-3.14 5.74-7.9 10.05z',
    // Icons.water_drop
    hydration:
      'M12 2c-5.33 4.55-8 8.48-8 11.8c0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zM7.83 14c.37 0 .67.26.74.62c.41 2.22 2.28 2.98 3.64 2.87c.43-.02.79.32.79.75c0 .4-.32.73-.72.75c-2.13.13-4.62-1.09-5.19-4.12a.75.75 0 0 1 .74-.87z',
    // Icons.wb_sunny_outlined
    sun_protection:
      'M6.76 4.84l-1.8-1.79l-1.41 1.41l1.79 1.79zM1 10.5h3v2H1zM11 .55h2V3.5h-2zm8.04 2.495l1.408 1.407l-1.79 1.79l-1.407-1.408zm-1.8 15.115l1.79 1.8l1.41-1.41l-1.8-1.79zM20 10.5h3v2h-3zm-8-5c-3.31 0-6 2.69-6 6s2.69 6 6 6s6-2.69 6-6s-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4s4 1.79 4 4s-1.79 4-4 4zm-1 4h2v2.95h-2zm-7.45-.96l1.41 1.41l1.79-1.8l-1.41-1.41z',
    sun_damage:
      'M6.76 4.84l-1.8-1.79l-1.41 1.41l1.79 1.79zM1 10.5h3v2H1zM11 .55h2V3.5h-2zm8.04 2.495l1.408 1.407l-1.79 1.79l-1.407-1.408zm-1.8 15.115l1.79 1.8l1.41-1.41l-1.8-1.79zM20 10.5h3v2h-3zm-8-5c-3.31 0-6 2.69-6 6s2.69 6 6 6s6-2.69 6-6s-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4s4 1.79 4 4s-1.79 4-4 4zm-1 4h2v2.95h-2zm-7.45-.96l1.41 1.41l1.79-1.8l-1.41-1.41z',
    // Icons.water_drop_outlined
    dryness:
      'M12 2c-5.33 4.55-8 8.48-8 11.8c0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2c0-2.34 1.95-5.44 6-9.14c4.05 3.7 6 6.79 6 9.14c0 3.63-2.65 6.2-6 6.2zm-4.17-6c.37 0 .67.26.74.62c.41 2.22 2.28 2.98 3.64 2.87c.43-.02.79.32.79.75c0 .4-.32.73-.72.75c-2.13.13-4.62-1.09-5.19-4.12a.75.75 0 0 1 .74-.87z',
    dry_hair:
      'M12 2c-5.33 4.55-8 8.48-8 11.8c0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2c0-2.34 1.95-5.44 6-9.14c4.05 3.7 6 6.79 6 9.14c0 3.63-2.65 6.2-6 6.2zm-4.17-6c.37 0 .67.26.74.62c.41 2.22 2.28 2.98 3.64 2.87c.43-.02.79.32.79.75c0 .4-.32.73-.72.75c-2.13.13-4.62-1.09-5.19-4.12a.75.75 0 0 1 .74-.87z',
    body_pigmentation:
      'M12 2c-5.33 4.55-8 8.48-8 11.8c0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2c0-2.34 1.95-5.44 6-9.14c4.05 3.7 6 6.79 6 9.14c0 3.63-2.65 6.2-6 6.2zm-4.17-6c.37 0 .67.26.74.62c.41 2.22 2.28 2.98 3.64 2.87c.43-.02.79.32.79.75c0 .4-.32.73-.72.75c-2.13.13-4.62-1.09-5.19-4.12a.75.75 0 0 1 .74-.87z',
    dry_lips:
      'M12 2c-5.33 4.55-8 8.48-8 11.8c0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2c0-2.34 1.95-5.44 6-9.14c4.05 3.7 6 6.79 6 9.14c0 3.63-2.65 6.2-6 6.2zm-4.17-6c.37 0 .67.26.74.62c.41 2.22 2.28 2.98 3.64 2.87c.43-.02.79.32.79.75c0 .4-.32.73-.72.75c-2.13.13-4.62-1.09-5.19-4.12a.75.75 0 0 1 .74-.87z',
    body_dryness:
      'M12 2c-5.33 4.55-8 8.48-8 11.8c0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2c0-2.34 1.95-5.44 6-9.14c4.05 3.7 6 6.79 6 9.14c0 3.63-2.65 6.2-6 6.2zm-4.17-6c.37 0 .67.26.74.62c.41 2.22 2.28 2.98 3.64 2.87c.43-.02.79.32.79.75c0 .4-.32.73-.72.75c-2.13.13-4.62-1.09-5.19-4.12a.75.75 0 0 1 .74-.87z',
    tooth_sensitivity:
      'M12 2c-5.33 4.55-8 8.48-8 11.8c0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2c0-2.34 1.95-5.44 6-9.14c4.05 3.7 6 6.79 6 9.14c0 3.63-2.65 6.2-6 6.2zm-4.17-6c.37 0 .67.26.74.62c.41 2.22 2.28 2.98 3.64 2.87c.43-.02.79.32.79.75c0 .4-.32.73-.72.75c-2.13.13-4.62-1.09-5.19-4.12a.75.75 0 0 1 .74-.87z',
    // Icons.brightness_low_outlined
    dullness:
      'M20 8.69V4h-4.69L12 .69L8.69 4H4v4.69L.69 12L4 15.31V20h4.69L12 23.31L15.31 20H20v-4.69L23.31 12L20 8.69zm-2 5.79V18h-3.52L12 20.48L9.52 18H6v-3.52L3.52 12L6 9.52V6h3.52L12 3.52L14.48 6H18v3.52L20.48 12L18 14.48zM12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6s6-2.69 6-6s-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4s4 1.79 4 4s-1.79 4-4 4z',
    // Icons.opacity
    oily_skin:
      'M17.66 8L12 2.35L6.34 8A8.02 8.02 0 0 0 4 13.64c0 2 .78 4.11 2.34 5.67a7.99 7.99 0 0 0 11.32 0c1.56-1.56 2.34-3.67 2.34-5.67S19.22 9.56 17.66 8zM6 14c.01-2 .62-3.27 1.76-4.4L12 5.27l4.24 4.38C17.38 10.77 17.99 12 18 14H6z',
    oily_scalp:
      'M17.66 8L12 2.35L6.34 8A8.02 8.02 0 0 0 4 13.64c0 2 .78 4.11 2.34 5.67a7.99 7.99 0 0 0 11.32 0c1.56-1.56 2.34-3.67 2.34-5.67S19.22 9.56 17.66 8zM6 14c.01-2 .62-3.27 1.76-4.4L12 5.27l4.24 4.38C17.38 10.77 17.99 12 18 14H6z',
    // Icons.gradient
    uneven_tone:
      'M11 9h2v2h-2zm-2 2h2v2H9zm4 0h2v2h-2zm2-2h2v2h-2zM7 9h2v2H7zm12-6H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 18H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm2-7h-2v2h2v2h-2v-2h-2v2h-2v-2h-2v2H9v-2H7v2H5v-2h2v-2H5V5h14v6z',
    // Icons.blur_circular
    pores:
      'M10 9c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm0 4c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zM7 9.5c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zm3 7c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zm-3-3c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zm3-6c.28 0 .5-.22.5-.5s-.22-.5-.5-.5s-.5.22-.5.5s.22.5.5.5zM14 9c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm0-1.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5s-.5.22-.5.5s.22.5.5.5zm3 6c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zm0-4c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8s8 3.58 8 8s-3.58 8-8 8zm2-3.5c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zm0-3.5c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1z',
    // Icons.brightness_6_outlined
    hyperpigmentation:
      'M20 8.69V4h-4.69L12 .69L8.69 4H4v4.69L.69 12L4 15.31V20h4.69L12 23.31L15.31 20H20v-4.69L23.31 12L20 8.69zm-2 5.79V18h-3.52L12 20.48L9.52 18H6v-3.52L3.52 12L6 9.52V6h3.52L12 3.52L14.48 6H18v3.52L20.48 12L18 14.48zM12 6.5v11c3.03 0 5.5-2.47 5.5-5.5S15.03 6.5 12 6.5z',
    melasma:
      'M20 8.69V4h-4.69L12 .69L8.69 4H4v4.69L.69 12L4 15.31V20h4.69L12 23.31L15.31 20H20v-4.69L23.31 12L20 8.69zm-2 5.79V18h-3.52L12 20.48L9.52 18H6v-3.52L3.52 12L6 9.52V6h3.52L12 3.52L14.48 6H18v3.52L20.48 12L18 14.48zM12 6.5v11c3.03 0 5.5-2.47 5.5-5.5S15.03 6.5 12 6.5z',
    // Icons.water
    excessive_sweating:
      'M21.98 14H22h-.02zM5.35 13c1.19 0 1.42 1 3.33 1c1.95 0 2.09-1 3.33-1c1.19 0 1.42 1 3.33 1c1.95 0 2.09-1 3.33-1c1.19 0 1.4.98 3.31 1v-2c-1.19 0-1.42-1-3.33-1c-1.95 0-2.09 1-3.33 1c-1.19 0-1.42-1-3.33-1c-1.95 0-2.09 1-3.33 1c-1.19 0-1.42-1-3.33-1c-1.95 0-2.09 1-3.33 1v2c1.9 0 2.17-1 3.35-1zm13.32 2c-1.95 0-2.09 1-3.33 1c-1.19 0-1.42-1-3.33-1c-1.95 0-2.1 1-3.34 1c-1.24 0-1.38-1-3.33-1c-1.95 0-2.1 1-3.34 1v2c1.95 0 2.11-1 3.34-1c1.24 0 1.38 1 3.33 1c1.95 0 2.1-1 3.34-1c1.19 0 1.42 1 3.33 1c1.94 0 2.09-1 3.33-1c1.19 0 1.42 1 3.33 1v-2c-1.24 0-1.38-1-3.33-1zM5.35 9c1.19 0 1.42 1 3.33 1c1.95 0 2.09-1 3.33-1c1.19 0 1.42 1 3.33 1c1.95 0 2.09-1 3.33-1c1.19 0 1.4.98 3.31 1V8c-1.19 0-1.42-1-3.33-1c-1.95 0-2.09 1-3.33 1c-1.19 0-1.42-1-3.33-1c-1.95 0-2.09 1-3.33 1c-1.19 0-1.42-1-3.33-1C3.38 7 3.24 8 2 8v2c1.9 0 2.17-1 3.35-1z',
    // Icons.timeline
    fine_lines:
      'M23 8c0 1.1-.9 2-2 2a1.7 1.7 0 0 1-.51-.07l-3.56 3.55c.05.16.07.34.07.52c0 1.1-.9 2-2 2s-2-.9-2-2c0-.18.02-.36.07-.52l-2.55-2.55c-.16.05-.34.07-.52.07s-.36-.02-.52-.07l-4.55 4.56c.05.16.07.33.07.51c0 1.1-.9 2-2 2s-2-.9-2-2s.9-2 2-2c.18 0 .35.02.51.07l4.56-4.55C8.02 9.36 8 9.18 8 9c0-1.1.9-2 2-2s2 .9 2 2c0 .18-.02.36-.07.52l2.55 2.55c.16-.05.34-.07.52-.07s.36.02.52.07l3.55-3.56A1.7 1.7 0 0 1 19 8c0-1.1.9-2 2-2s2 .9 2 2z',
    // Icons.texture
    wrinkles:
      'M19.51 3.08L3.08 19.51c.09.34.27.65.51.9c.25.24.56.42.9.51L20.93 4.49c-.19-.69-.73-1.23-1.42-1.41zM11.88 3L3 11.88v2.83L14.71 3h-2.83zM5 3c-1.1 0-2 .9-2 2v2l4-4H5zm14 18c.55 0 1.05-.22 1.41-.59c.37-.36.59-.86.59-1.41v-2l-4 4h2zm-9.71 0h2.83L21 12.12V9.29L9.29 21z',
    rough_texture:
      'M19.51 3.08L3.08 19.51c.09.34.27.65.51.9c.25.24.56.42.9.51L20.93 4.49c-.19-.69-.73-1.23-1.42-1.41zM11.88 3L3 11.88v2.83L14.71 3h-2.83zM5 3c-1.1 0-2 .9-2 2v2l4-4H5zm14 18c.55 0 1.05-.22 1.41-.59c.37-.36.59-.86.59-1.41v-2l-4 4h2zm-9.71 0h2.83L21 12.12V9.29L9.29 21z',
    cellulite:
      'M19.51 3.08L3.08 19.51c.09.34.27.65.51.9c.25.24.56.42.9.51L20.93 4.49c-.19-.69-.73-1.23-1.42-1.41zM11.88 3L3 11.88v2.83L14.71 3h-2.83zM5 3c-1.1 0-2 .9-2 2v2l4-4H5zm14 18c.55 0 1.05-.22 1.41-.59c.37-.36.59-.86.59-1.41v-2l-4 4h2zm-9.71 0h2.83L21 12.12V9.29L9.29 21z',
    rough_elbows_knees:
      'M19.51 3.08L3.08 19.51c.09.34.27.65.51.9c.25.24.56.42.9.51L20.93 4.49c-.19-.69-.73-1.23-1.42-1.41zM11.88 3L3 11.88v2.83L14.71 3h-2.83zM5 3c-1.1 0-2 .9-2 2v2l4-4H5zm14 18c.55 0 1.05-.22 1.41-.59c.37-.36.59-.86.59-1.41v-2l-4 4h2zm-9.71 0h2.83L21 12.12V9.29L9.29 21z',
    // Icons.local_fire_department_outlined
    redness:
      'M16 6l-.44.55c-.42.52-.98.75-1.54.75C13 7.3 12 6.52 12 5.3V2S4 6 4 13c0 4.42 3.58 8 8 8s8-3.58 8-8c0-2.96-1.61-5.62-4-7zm-4 13c-1.1 0-2-.87-2-1.94c0-.51.2-.99.58-1.36L12 14.3l1.43 1.4c.37.37.57.85.57 1.36c0 1.07-.9 1.94-2 1.94zm3.96-1.5c.04-.36.22-1.89-1.13-3.22L12 11.5l-2.83 2.78C7.81 15.62 8 17.16 8.04 17.5A5.982 5.982 0 0 1 6 13c0-3.16 2.13-5.65 4.03-7.25a4.024 4.024 0 0 0 3.99 3.55c.78 0 1.54-.23 2.18-.66A6.175 6.175 0 0 1 18 13c0 1.79-.79 3.4-2.04 4.5z',
    rosacea:
      'M16 6l-.44.55c-.42.52-.98.75-1.54.75C13 7.3 12 6.52 12 5.3V2S4 6 4 13c0 4.42 3.58 8 8 8s8-3.58 8-8c0-2.96-1.61-5.62-4-7zm-4 13c-1.1 0-2-.87-2-1.94c0-.51.2-.99.58-1.36L12 14.3l1.43 1.4c.37.37.57.85.57 1.36c0 1.07-.9 1.94-2 1.94zm3.96-1.5c.04-.36.22-1.89-1.13-3.22L12 11.5l-2.83 2.78C7.81 15.62 8 17.16 8.04 17.5A5.982 5.982 0 0 1 6 13c0-3.16 2.13-5.65 4.03-7.25a4.024 4.024 0 0 0 3.99 3.55c.78 0 1.54-.23 2.18-.66A6.175 6.175 0 0 1 18 13c0 1.79-.79 3.4-2.04 4.5z',
    // Icons.grain
    acne:
      'M10 12c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zM6 8c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm0 8c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm12-8c1.1 0 2-.9 2-2s-.9-2-2-2s-2 .9-2 2s.9 2 2 2zm-4 8c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm4-4c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm-4-4c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm-4-4c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2z',
    body_acne:
      'M10 12c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zM6 8c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm0 8c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm12-8c1.1 0 2-.9 2-2s-.9-2-2-2s-2 .9-2 2s.9 2 2 2zm-4 8c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm4-4c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm-4-4c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2zm-4-4c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2z',
    // Icons.remove_circle_outline
    hair_loss:
      'M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8z',
    // Icons.trending_up
    hair_growth:
      'M16 6l2.29 2.29l-4.88 4.88l-4-4L2 16.59L3.41 18l6-6l4 4l6.3-6.29L22 12V6z',
    // Icons.dark_mode_outlined
    dark_circles:
      'M9.37 5.51A7.35 7.35 0 0 0 9.1 7.5c0 4.08 3.32 7.4 7.4 7.4c.68 0 1.35-.09 1.99-.27A7.014 7.014 0 0 1 12 19c-3.86 0-7-3.14-7-7c0-2.93 1.81-5.45 4.37-6.49zM12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 0 1-4.4 2.26a5.403 5.403 0 0 1-3.14-9.8c-.44-.06-.9-.1-1.36-.1z',
    // Icons.healing
    damaged_hair:
      'M17.73 12.02l3.98-3.98a.996.996 0 0 0 0-1.41l-4.34-4.34a.996.996 0 0 0-1.41 0l-3.98 3.98L8 2.29a1.001 1.001 0 0 0-1.41 0L2.25 6.63a.996.996 0 0 0 0 1.41l3.98 3.98L2.25 16a.996.996 0 0 0 0 1.41l4.34 4.34c.39.39 1.02.39 1.41 0l3.98-3.98l3.98 3.98c.2.2.45.29.71.29c.26 0 .51-.1.71-.29l4.34-4.34a.996.996 0 0 0 0-1.41l-3.99-3.98zM12 9c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1zm-4.71 1.96L3.66 7.34l3.63-3.63l3.62 3.62l-3.62 3.63zM10 13c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1zm2 2c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1zm2-4c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1zm2.66 9.34l-3.63-3.62l3.63-3.63l3.62 3.62l-3.62 3.63z',
    eczema:
      'M17.73 12.02l3.98-3.98a.996.996 0 0 0 0-1.41l-4.34-4.34a.996.996 0 0 0-1.41 0l-3.98 3.98L8 2.29a1.001 1.001 0 0 0-1.41 0L2.25 6.63a.996.996 0 0 0 0 1.41l3.98 3.98L2.25 16a.996.996 0 0 0 0 1.41l4.34 4.34c.39.39 1.02.39 1.41 0l3.98-3.98l3.98 3.98c.2.2.45.29.71.29c.26 0 .51-.1.71-.29l4.34-4.34a.996.996 0 0 0 0-1.41l-3.99-3.98zM12 9c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1zm-4.71 1.96L3.66 7.34l3.63-3.63l3.62 3.62l-3.62 3.63zM10 13c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1zm2 2c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1zm2-4c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1zm2.66 9.34l-3.63-3.62l3.63-3.63l3.62 3.62l-3.62 3.63z',
    // Icons.healing (same as eczema in the app)
    psoriasis:
      'M17.73 12.02l3.98-3.98a.996.996 0 0 0 0-1.41l-4.34-4.34a.996.996 0 0 0-1.41 0l-3.98 3.98L8 2.29a1.001 1.001 0 0 0-1.41 0L2.25 6.63a.996.996 0 0 0 0 1.41l3.98 3.98L2.25 16a.996.996 0 0 0 0 1.41l4.34 4.34c.39.39 1.02.39 1.41 0l3.98-3.98l3.98 3.98c.2.2.45.29.71.29c.26 0 .51-.1.71-.29l4.34-4.34a.996.996 0 0 0 0-1.41l-3.99-3.98zM12 9c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1zm-4.71 1.96L3.66 7.34l3.63-3.63l3.62 3.62l-3.62 3.63zM10 13c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1zm2 2c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1zm2-4c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1zm2.66 9.34l-3.63-3.62l3.63-3.63l3.62 3.62l-3.62 3.63z',
    // Icons.call_split
    split_ends:
      'M14 4l2.29 2.29l-2.88 2.88l1.42 1.42l2.88-2.88L20 10V4zm-4 0H4v6l2.29-2.29l4.71 4.7V20h2v-8.41l-5.29-5.3z',
    // Icons.scatter_plot
    blackheads:
      'M7,14 m-3,0 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 M11,6 m-3,0 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 M16.6,17.6 m-3,0 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0',
    // Icons.blur_on
    dandruff:
      'M6 13c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm0 4c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm0-8c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm-3 .5c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zM6 5c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm15 5.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5s-.5.22-.5.5s.22.5.5.5zM14 7c.55 0 1-.45 1-1s-.45-1-1-1s-1 .45-1 1s.45 1 1 1zm0-3.5c.28 0 .5-.22.5-.5s-.22-.5-.5-.5s-.5.22-.5.5s.22.5.5.5zm-11 10c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zm7 7c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zm0-17c.28 0 .5-.22.5-.5s-.22-.5-.5-.5s-.5.22-.5.5s.22.5.5.5zM10 7c.55 0 1-.45 1-1s-.45-1-1-1s-1 .45-1 1s.45 1 1 1zm0 5.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5s1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5zm8 .5c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm0 4c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm0-8c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm0-4c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm3 8.5c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zM14 17c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm0 3.5c-.28 0-.5.22-.5.5s.22.5.5.5s.5-.22.5-.5s-.22-.5-.5-.5zm-4-12c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5s1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5zm0 8.5c-.55 0-1 .45-1 1s.45 1 1 1s1-.45 1-1s-.45-1-1-1zm4-4.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5s1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5zm0-4c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5s1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5z',
    // Icons.visibility_outlined
    puffiness:
      'M12 6a9.77 9.77 0 0 1 8.82 5.5C19.17 14.87 15.79 17 12 17s-7.17-2.13-8.82-5.5A9.77 9.77 0 0 1 12 6m0-2C7 4 2.73 7.11 1 11.5C2.73 15.89 7 19 12 19s9.27-3.11 11-7.5C21.27 7.11 17 4 12 4zm0 5a2.5 2.5 0 0 1 0 5a2.5 2.5 0 0 1 0-5m0-2c-2.48 0-4.5 2.02-4.5 4.5S9.52 16 12 16s4.5-2.02 4.5-4.5S14.48 7 12 7z',
    sensitive_eyes:
      'M12 6a9.77 9.77 0 0 1 8.82 5.5C19.17 14.87 15.79 17 12 17s-7.17-2.13-8.82-5.5A9.77 9.77 0 0 1 12 6m0-2C7 4 2.73 7.11 1 11.5C2.73 15.89 7 19 12 19s9.27-3.11 11-7.5C21.27 7.11 17 4 12 4zm0 5a2.5 2.5 0 0 1 0 5a2.5 2.5 0 0 1 0-5m0-2c-2.48 0-4.5 2.02-4.5 4.5S9.52 16 12 16s4.5-2.02 4.5-4.5S14.48 7 12 7z',
    // Icons.linear_scale
    stretch_marks:
      'M19.5 9.5c-1.03 0-1.9.62-2.29 1.5h-2.92c-.39-.88-1.26-1.5-2.29-1.5s-1.9.62-2.29 1.5H6.79c-.39-.88-1.26-1.5-2.29-1.5a2.5 2.5 0 0 0 0 5c1.03 0 1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5s1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5a2.5 2.5 0 0 0 0-5z',
    // Icons.change_history
    acne_scars:
      'M12 7.77L18.39 18H5.61L12 7.77M12 4L2 20h20L12 4z',
    // Icons.air
    frizz:
      'M14.5 17c0 1.65-1.35 3-3 3s-3-1.35-3-3h2c0 .55.45 1 1 1s1-.45 1-1s-.45-1-1-1H2v-2h9.5c1.65 0 3 1.35 3 3zM19 6.5C19 4.57 17.43 3 15.5 3S12 4.57 12 6.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S16.33 8 15.5 8H2v2h13.5c1.93 0 3.5-1.57 3.5-3.5zm-.5 4.5H2v2h16.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5v2c1.93 0 3.5-1.57 3.5-3.5S20.43 11 18.5 11z',
    body_odor:
      'M14.5 17c0 1.65-1.35 3-3 3s-3-1.35-3-3h2c0 .55.45 1 1 1s1-.45 1-1s-.45-1-1-1H2v-2h9.5c1.65 0 3 1.35 3 3zM19 6.5C19 4.57 17.43 3 15.5 3S12 4.57 12 6.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S16.33 8 15.5 8H2v2h13.5c1.93 0 3.5-1.57 3.5-3.5zm-.5 4.5H2v2h16.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5v2c1.93 0 3.5-1.57 3.5-3.5S20.43 11 18.5 11z',
    bad_breath:
      'M14.5 17c0 1.65-1.35 3-3 3s-3-1.35-3-3h2c0 .55.45 1 1 1s1-.45 1-1s-.45-1-1-1H2v-2h9.5c1.65 0 3 1.35 3 3zM19 6.5C19 4.57 17.43 3 15.5 3S12 4.57 12 6.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S16.33 8 15.5 8H2v2h13.5c1.93 0 3.5-1.57 3.5-3.5zm-.5 4.5H2v2h16.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5v2c1.93 0 3.5-1.57 3.5-3.5S20.43 11 18.5 11z',
    // Icons.line_weight
    hair_thinning:
      'M3 17h18v-2H3v2zm0 3h18v-1H3v1zm0-7h18v-3H3v3zm0-9v4h18V4H3z',
    // Icons.trending_down
    sagging:
      'M16 18l2.29-2.29l-4.88-4.88l-4 4L2 7.41L3.41 6l6 6l4-4l6.3 6.29L22 12v6z',
    // Icons.content_cut
    ingrown_hairs:
      'M9.64 7.64c.23-.5.36-1.05.36-1.64c0-2.21-1.79-4-4-4S2 3.79 2 6s1.79 4 4 4c.59 0 1.14-.13 1.64-.36L10 12l-2.36 2.36C7.14 14.13 6.59 14 6 14c-2.21 0-4 1.79-4 4s1.79 4 4 4s4-1.79 4-4c0-.59-.13-1.14-.36-1.64L12 14l7 7h3v-1L9.64 7.64zM6 8c-1.1 0-2-.89-2-2s.9-2 2-2s2 .89 2 2s-.9 2-2 2zm0 12c-1.1 0-2-.89-2-2s.9-2 2-2s2 .89 2 2s-.9 2-2 2zm6-7.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5s.5.22.5.5s-.22.5-.5.5zM19 3l-6 6l2 2l7-7V3z',
    // Icons.medical_services_outlined
    cavities:
      'M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM10 4h4v2h-4V4zm10 16H4V8h16v12z M13 10h-2v3H8v2h3v3h2v-3h3v-2h-3z',
    // Icons.cleaning_services_outlined
    plaque:
      'M16 11h-1V3c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2v8H8c-2.76 0-5 2.24-5 5v7h18v-7c0-2.76-2.24-5-5-5zm-5-8h2v8h-2V3zm8 18h-2v-3c0-.55-.45-1-1-1s-1 .45-1 1v3h-2v-3c0-.55-.45-1-1-1s-1 .45-1 1v3H9v-3c0-.55-.45-1-1-1s-1 .45-1 1v3H5v-5c0-1.65 1.35-3 3-3h8c1.65 0 3 1.35 3 3v5z',
    // Icons.brightness_7_outlined
    tooth_whitening:
      'M20 8.69V4h-4.69L12 .69L8.69 4H4v4.69L.69 12L4 15.31V20h4.69L12 23.31L15.31 20H20v-4.69L23.31 12L20 8.69zm-2 5.79V18h-3.52L12 20.48L9.52 18H6v-3.52L3.52 12L6 9.52V6h3.52L12 3.52L14.48 6H18v3.52L20.48 12L18 14.48zM12 6.5c-3.03 0-5.5 2.47-5.5 5.5s2.47 5.5 5.5 5.5s5.5-2.47 5.5-5.5s-2.47-5.5-5.5-5.5zm0 9c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5s3.5 1.57 3.5 3.5s-1.57 3.5-3.5 3.5z',
  };

  function svgForConcern(code) {
    return CONCERN_ICON_PATHS[code] || ICON_PATHS.droplet;
  }

  function openTrustPopover(title, body) {
    var text = String(body || '').trim();
    if (!text) return;
    var heading = String(title || '').trim() || text;
    if (window.ErosUI && typeof window.ErosUI.openSheet === 'function') {
      var content = document.createElement('div');
      content.className = 'eros-pdp-trust__popover-body';
      var p = document.createElement('p');
      p.textContent = text;
      content.appendChild(p);
      window.ErosUI.openSheet({ title: heading, content: content });
      return;
    }
    /* Fallback dialog when commerce UI not loaded */
    var dialog = document.createElement('dialog');
    dialog.className = 'eros-pdp-trust__dialog color-scheme color-scheme--dialog';
    dialog.innerHTML =
      '<form method="dialog" class="eros-pdp-trust__dialog-inner">' +
      '<p class="h4 eros-pdp-trust__dialog-title"></p>' +
      '<p class="eros-pdp-trust__dialog-text"></p>' +
      '<button type="submit" class="button">OK</button>' +
      '</form>';
    dialog.querySelector('.eros-pdp-trust__dialog-title').textContent = heading;
    dialog.querySelector('.eros-pdp-trust__dialog-text').textContent = text;
    document.body.appendChild(dialog);
    dialog.addEventListener('close', function () {
      dialog.remove();
    });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });
    if (typeof dialog.showModal === 'function') dialog.showModal();
  }

  function bindTrustCircle(btn) {
    if (!btn || btn.dataset.erosTrustBound === '1') return;
    btn.dataset.erosTrustBound = '1';
    btn.addEventListener('click', function (event) {
      event.preventDefault();
      var cell = btn.closest('.eros-pdp-trust__cell');
      var labelEl = cell ? cell.querySelector('.eros-pdp-trust__label') : null;
      var label = (labelEl && labelEl.textContent) || btn.getAttribute('aria-label') || '';
      var tip =
        btn.getAttribute('data-eros-trust-tip') ||
        btn.getAttribute('title') ||
        btn.getAttribute('aria-label') ||
        label;
      openTrustPopover(label, tip);
    });
  }

  function bindAllTrustCircles(root) {
    var scope = root || document;
    scope.querySelectorAll('.eros-pdp-trust__circle').forEach(bindTrustCircle);
  }

  function buildCell(label, tooltip, code) {
    var cell = document.createElement('div');
    cell.className = 'eros-pdp-trust__cell';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'eros-pdp-trust__circle';
    btn.setAttribute('title', tooltip || label);
    btn.setAttribute('aria-label', tooltip || label);
    btn.setAttribute('data-eros-trust-tip', tooltip || label);

    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', svgForConcern(code));
    svg.appendChild(path);
    btn.appendChild(svg);

    var span = document.createElement('span');
    span.className = 'eros-pdp-trust__label';
    span.textContent = label;

    cell.appendChild(btn);
    cell.appendChild(span);
    bindTrustCircle(btn);
    return cell;
  }

  function pruneEmptyRoot(root) {
    if (!root) return;
    var host = root.querySelector('[data-eros-trust-concerns-host]');
    var hostEmpty = !host || host.hidden || !host.querySelector('.eros-pdp-trust__cell');
    var liquidCount = 0;
    var grids = root.querySelectorAll('.eros-pdp-trust__inner > .eros-pdp-trust__grid');
    for (var i = 0; i < grids.length; i++) {
      liquidCount += grids[i].querySelectorAll('.eros-pdp-trust__cell').length;
    }
    if (hostEmpty && liquidCount === 0) {
      root.hidden = true;
    }
  }

  function renderHost(host, items) {
    host.innerHTML = '';
    if (!items.length) {
      host.hidden = true;
      pruneEmptyRoot(host.closest('[data-eros-pdp-trust]'));
      return;
    }
    host.hidden = false;
    var grid = document.createElement('div');
    grid.className = 'eros-pdp-trust__grid';
    for (var i = 0; i < items.length; i++) {
      grid.appendChild(buildCell(items[i].label, items[i].tooltip, items[i].code));
    }
    host.appendChild(grid);
  }

  var dictPromise = null;

  function loadDictionary(url) {
    if (!url) return Promise.resolve(null);
    if (!dictPromise) {
      dictPromise = fetch(url, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('dictionary ' + res.status);
          return res.json();
        })
        .catch(function () {
          return null;
        });
    }
    return dictPromise;
  }

  function mountRoot(root) {
    var host = root.querySelector('[data-eros-trust-concerns-host]');
    if (!host) return;

    /*
     * Server-rendered concern chips (sections/eros-pdp-trust-badges.liquid)
     * already contain the final markup — same classes, same icons, same
     * localized copy the dictionary fetch below would have produced. Skip
     * the fetch + re-render entirely so we never wipe/duplicate the SSR
     * content. `data-eros-trust-ssr="1"` is the primary signal; the cell
     * check is a defensive fallback in case that attribute is ever missing.
     */
    if (host.hasAttribute('data-eros-trust-ssr') || host.querySelector('.eros-pdp-trust__cell')) {
      host.hidden = false;
      return;
    }

    var concerns = parseConcerns(root.getAttribute('data-concerns'));
    if (!concerns.length) {
      host.hidden = true;
      return;
    }

    var bucket = pdpConcernBucket(
      root.getAttribute('data-section'),
      root.getAttribute('data-type')
    );
    var url = root.getAttribute('data-dictionary-url');

    loadDictionary(url).then(function (dict) {
      var entries = dict && dict.entries ? dict.entries : null;
      if (!entries) {
        host.hidden = true;
        pruneEmptyRoot(root);
        return;
      }
      var seen = Object.create(null);
      var items = [];
      for (var i = 0; i < concerns.length; i++) {
        var code = String(concerns[i] || '').trim();
        if (!code || seen[code]) continue;
        seen[code] = 1;
        var copy = resolveCopy(entries, code, bucket);
        if (!copy) continue;
        items.push({
          label: labelFromCopy(copy),
          tooltip: tooltipFromCopy(copy, labelFromCopy(copy)),
          code: code,
        });
      }
      renderHost(host, items);
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-eros-pdp-trust]');
    for (var i = 0; i < roots.length; i++) mountRoot(roots[i]);
    bindAllTrustCircles(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event || !event.target) return;
    var roots = event.target.querySelectorAll
      ? event.target.querySelectorAll('[data-eros-pdp-trust]')
      : [];
    for (var i = 0; i < roots.length; i++) mountRoot(roots[i]);
    bindAllTrustCircles(event.target);
  });
})();
